import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatFechaCorta, resolveVentanaEmisionReceta } from '../utils/tratamientoDosificacion';

const POPOVER_WIDTH = 280;
const VIEWPORT_GAP = 12;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function RecetaEmisionVentanaPin({
  fechaPrevista,
  diasPasado = 5,
  diasFuturo = 5,
  align = 'right'
}) {
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const ventana = useMemo(
    () => resolveVentanaEmisionReceta(fechaPrevista, { diasPasado, diasFuturo }),
    [fechaPrevista, diasPasado, diasFuturo]
  );

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const rect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const popW = popRect.width || POPOVER_WIDTH;
    const popH = popRect.height || 0;

    let left = align === 'left' ? rect.left : rect.right - popW;
    left = clamp(left, VIEWPORT_GAP, window.innerWidth - popW - VIEWPORT_GAP);

    let top = rect.top - popH - 10;
    let placement = 'above';
    if (top < VIEWPORT_GAP) {
      top = rect.bottom + 10;
      placement = 'below';
    }

    setCoords({ left, top, placement });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(raf);
  }, [open, updatePosition, ventana]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => updatePosition();
    const handlePointerDown = (event) => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (anchor?.contains(event.target) || popover?.contains(event.target)) return;
      setOpen(false);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open, updatePosition]);

  if (!ventana?.desde || !ventana?.hasta) return null;

  const estadoClass = ventana.estado === 'activa'
    ? ' crm-re-emit-pin--activa'
    : ventana.estado === 'futura'
      ? ' crm-re-emit-pin--futura'
      : ' crm-re-emit-pin--vencida';

  const popover = open && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={popoverRef}
        className={`crm-re-emit-pin__pop crm-re-emit-pin__pop--portal${coords?.placement === 'below' ? ' crm-re-emit-pin__pop--below' : ''}`}
        style={coords ? { left: coords.left, top: coords.top } : { visibility: 'hidden' }}
        role="tooltip"
      >
        <span className="crm-re-emit-pin__pop-title">Ventana de emisión</span>
        <span className={`crm-re-emit-pin__estado crm-re-emit-pin__estado--${ventana.estado}`}>
          {ventana.estadoLabel}
        </span>
        <span className="crm-re-emit-pin__range">
          <span className="crm-re-emit-pin__range-label">Desde</span>
          <strong>{formatFechaCorta(ventana.desde)}</strong>
          <span className="crm-re-emit-pin__range-arrow">→</span>
          <span className="crm-re-emit-pin__range-label">Hasta</span>
          <strong>{formatFechaCorta(ventana.hasta)}</strong>
        </span>
        <span className="crm-re-emit-pin__hint">
          Fecha prevista: <strong>{formatFechaCorta(ventana.fechaPrevista)}</strong>
          {' · '}
          {diasPasado} d atrás / {diasFuturo} d adelante
        </span>
      </div>,
      document.body
    )
    : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={`crm-re-emit-pin${estadoClass}${open ? ' crm-re-emit-pin--open' : ''} crm-re-emit-pin--${align}`}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        aria-label={`Ventana de emisión: del ${formatFechaCorta(ventana.desde)} al ${formatFechaCorta(ventana.hasta)}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span className="crm-re-emit-pin__icon" aria-hidden="true">📅</span>
      </span>
      {popover}
    </>
  );
}
